// ════════════════════════════════════════════════════════════
//  Spread.jsx — THE SPREAD: the odds on a fight, behind the Odds button
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-16: "a 'spread' board to show you the odds of your bets
//  if you click on the odds button".
//  Two kinds of odds, side by side, honestly labelled:
//   · WILLIE'S LINE — a guess from the ratings (who beat whom), as a chance
//     and as what a fair book would pay. Never a promise.
//   · THE MONEY — what the pool would actually pay you right now (players
//     vs players, no house — the bet panel's own maths).
//  Plus what the line is made of: rating, record, form, head to head.
// ════════════════════════════════════════════════════════════
import { useCircuit } from "./circuitStore.js";
import { statOf, willieLine, headToHead, avgWin } from "../online/circuit.js";

const pct = (p) => `${Math.round(p * 100)}%`;
const mmss = (s) => (s >= 1e8 ? "—" : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`);

export default function Spread({ keys, names, total }) {
  const C = useCircuit(true);
  if (!keys || keys.length !== 2) return null;
  const [a, b] = keys.map((k) => statOf(C.S, k));
  const line = willieLine(a, b);
  const h2h = headToHead(C.rows, keys[0], keys[1]);
  const pays = (s) => (total && total[s] ? (1 + total[1 - s] / total[s]) : null);
  const fav = line.p[0] === line.p[1] ? null : line.p[0] > line.p[1] ? 0 : 1;
  const rows = [
    ["Rating", (x) => Math.round(x.elo)],
    ["The table", (x) => `${x.lw}–${x.ll}`],
    ["Every bout", (x) => `${x.won}–${x.lost}`],
    ["Avg win", (x) => mmss(avgWin(x))],
    ["Streak", (x) => (x.streak > 0 ? `W${x.streak}` : x.streak < 0 ? `L${-x.streak}` : "—")],
  ];
  return (
    <div className="sp-spread">
      <div className="sp-h">The spread <small>{C.ready ? "Willie's line is a guess from form — never a promise" : "loading the form…"}</small></div>
      <div className="sp-bar" aria-label="Willie's line">
        <span className="s0" style={{ flexBasis: pct(line.p[0]) }}>{pct(line.p[0])}</span>
        <span className="s1" style={{ flexBasis: pct(line.p[1]) }}>{pct(line.p[1])}</span>
      </div>
      <table className="sp-table">
        <thead><tr><th /><th className="s0">{names[0]}{fav === 0 && <em> fav</em>}</th><th className="s1">{names[1]}{fav === 1 && <em> fav</em>}</th></tr></thead>
        <tbody>
          <tr><td title="What a fair book would pay on Willie's numbers — the pool pays what the money says">Fair price</td><td>×{line.fair[0].toFixed(2)}</td><td>×{line.fair[1].toFixed(2)}</td></tr>
          <tr><td>The money pays</td><td>{pays(0) ? `×${pays(0).toFixed(2)}` : "no bets yet"}</td><td>{pays(1) ? `×${pays(1).toFixed(2)}` : "no bets yet"}</td></tr>
          {rows.map(([label, f]) => <tr key={label}><td>{label}</td><td>{f(a)}</td><td>{f(b)}</td></tr>)}
          <tr><td>Form</td>{[a, b].map((x, s) => <td key={s} className="sp-form">{x.form.slice(-5).map((r, i) => <i key={i} className={r} />)}{!x.form.length && "new face"}</td>)}</tr>
          <tr><td>Head to head</td><td colSpan={2}>{h2h[0] + h2h[1] ? `${h2h[0]}–${h2h[1]} this week` : "never met this week"}</td></tr>
        </tbody>
      </table>
    </div>
  );
}
